import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CompanyLogoAndNameResponse } from '../../models/fundamental/types';
import { LoggerService } from '../../services/logger/Logger.service';
import { AppStateStatus } from '../../store-util/AppStateCache';
import { RECENTLY_VIEWED_SYMBOLS_MAX_LIMIT } from '../../util/constants';
import { isEmpty } from '../../util/typeGuards';
import { DEFAULT_USER_PREFERENCE_DATA } from '../app/constants';
import { encodeUserPreferencesData } from '../app/helpers';
import {
  addOrRemoveSymbolToFavoriteList,
  addSymbolToRecentlyViewed,
  DisconnectedPayload,
  loginFailed,
  logout,
  marketDataDisconnected,
  setAppStatus,
} from '../common-actions';

import {
  AddSymbolToRecentlyViewedPayload,
  SymbolFavoriteListActionType,
  SymbolFavoriteListPayload,
  UserFavoriteStocks,
  UserRecentlyViewedStocks,
} from './types';

export type MarketDataState = {
  connected: boolean;
  favouriteStocks: UserFavoriteStocks;
  recentlyViewedStocks: UserRecentlyViewedStocks;
  companies: {[key: string]: string};
  /**
   * For reference of Market State times see:
   *  (1) Google Presentation - https://docs.google.com/presentation/d/17j2erZF0NBqA67IF71b8ub0JK5p7-__OPhYCpyMRd00/edit#slide=id.g10947438435_0_36
   *  (2) Library constants at `alaric-retail-terminal-library/constants/marketHours.ts`
   */
  openPositionsSubscribed: boolean;
  isInitialPriceDataLoaded: boolean;
}
export const INITIAL_STATE: MarketDataState = {
  connected: false,
  favouriteStocks: {},
  recentlyViewedStocks: {},
  companies: {}, // TODO: Remove this quickfix for reloading watchlist
  openPositionsSubscribed: false,
  /**
   * Marks if the main groups of symbols have price data loaded:
   *  1. WatchList (popular & favorites)
   *  2. Discover (top gainers, top losers, new on the market)
   *  3. My Account (my stocks - open positions)
   */
  isInitialPriceDataLoaded: false,
};

const logger = LoggerService.getLogger();

const marketDataSlice = createSlice({
  name: 'marketData',
  initialState: INITIAL_STATE,
  reducers: {
    retrieveFavoriteStocksFromServerCompleted(state, action) {
      const { data, customData } = action.payload;
      const { userId } = customData;
      const { favouriteStocks, recentlyViewedStocks } = encodeUserPreferencesData(data);
      state.favouriteStocks[userId] = isEmpty(favouriteStocks)
        ? DEFAULT_USER_PREFERENCE_DATA.favouriteStocks
        : favouriteStocks;
      state.recentlyViewedStocks[userId] = isEmpty(recentlyViewedStocks)
        ? DEFAULT_USER_PREFERENCE_DATA.recentlyViewedStocks
        : recentlyViewedStocks;
    },
    connected(state) { state.connected = true; },
    /** Dispatched only by InitialSymbolsDataLoadingStatusCache.updateStatus */
    confirmInitialPriceDataLoaded(state) {
      state.isInitialPriceDataLoaded = true;
    },
    /**
     * The payload of the action has `symbols` - this is one symbol or comma separated list as string
     */
    addCompanyToCache(state: MarketDataState, action: PayloadAction<{
      responseData: CompanyLogoAndNameResponse[], symbols: string
    }>) {
      const companies = action.payload.responseData ?? [];
      const { symbols } = action.payload;

      if (companies.length > 0) {
        const symbolsArray: string[] = symbols.split(',');

        symbolsArray.forEach(item => {
          const { symbol, name } = companies.find(c => c.symbol === item) || {};

          if (symbol && name && !state.companies[item]) {
            state.companies[symbol] = name;
          } else if (!state.companies[symbol!]) {
            console.warn('[market-data/index] addCompanyToCache - could not add company to cache', { symbol, name });
          }
        });
      }
    },
  },
  extraReducers: {
    [setAppStatus.type]: (
      state,
      action: PayloadAction<AppStateStatus>,
    ) => {
      if (action.payload === 'background') {
        state.isInitialPriceDataLoaded = false;
      }
    },
    [loginFailed.type]: state => { state.isInitialPriceDataLoaded = false; },
    [marketDataDisconnected.type]: (
      state,
      action: PayloadAction<DisconnectedPayload>,
    ) => {
      state.connected = false;
      if (action.payload && action.payload?.message) {
        logger.error(action.payload.message);
      }
    },
    [logout.type]: state => ({
      ...INITIAL_STATE,
      favouriteStocks: state.favouriteStocks,
      recentlyViewedStocks: state.recentlyViewedStocks,
    }),
    [addSymbolToRecentlyViewed.type]: (
      state,
      action: PayloadAction<AddSymbolToRecentlyViewedPayload>,
    ) => {
      const { symbolAndExchange, userCorrelationId } = action.payload;
      const { symbol, stockExchangeCode, countryDesignation } = symbolAndExchange ?? {};

      if (!userCorrelationId) {
        return;
      }

      const existing = state.recentlyViewedStocks[userCorrelationId] ?? [];
      const filtered = existing.filter(item => item.symbol !== symbol);
      const updated = [ { symbol, stockExchangeCode, countryDesignation }, ...filtered ];
      state.recentlyViewedStocks[userCorrelationId] = updated.slice(0, RECENTLY_VIEWED_SYMBOLS_MAX_LIMIT);
    },
    [addOrRemoveSymbolToFavoriteList.type]: (
      state,
      action: PayloadAction<SymbolFavoriteListPayload>,
    ) => {
      const { symbolAndExchange, actionType, userCorrelationId } = action.payload;
      const { symbol, stockExchangeCode, countryDesignation } = symbolAndExchange ?? {};
      const isRemoving = actionType === SymbolFavoriteListActionType.Remove;

      if (!userCorrelationId) {
        return;
      }

      if (isRemoving) {
        const favouriteStock = state.favouriteStocks[userCorrelationId!];
        if (favouriteStock) {
          const filteredFavouriteStocks = favouriteStock
            .filter(item => item.symbol !== symbol);
          state.favouriteStocks[userCorrelationId] = filteredFavouriteStocks;
        }
        return;
      }

      if (!state.favouriteStocks[userCorrelationId]) {
        state.favouriteStocks[userCorrelationId] = [ ...[], { symbol, stockExchangeCode, countryDesignation } ];
      } else {
        state.favouriteStocks[userCorrelationId] = [
          ...state.favouriteStocks[userCorrelationId], { symbol, stockExchangeCode, countryDesignation },
        ];
      }
    },
  },
});

export const {
  connected,
  confirmInitialPriceDataLoaded,
  addCompanyToCache,
  retrieveFavoriteStocksFromServerCompleted,
} = marketDataSlice.actions;

export default marketDataSlice.reducer;
